/**
 * AI Conversation Controls Component
 * ===================================
 * Provides controls for toggling AI and manual escalation
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Stack
} from '@mui/material';
import {
  SmartToy,
  Person,
  MoreVert,
  PersonAdd,
  Warning,
  AutoMode,
  Settings
} from '@mui/icons-material';
import api from '../../services/api';

export interface HandoffState {
  ai_enabled: boolean;
  escalated: boolean;
  escalated_at?: string | null;
  escalated_by?: string | null;
  escalation_reason?: string | null;
  escalation_trigger?: string | null;
  assignee_id?: number | null;
}

interface AIConversationControlsProps {
  conversationId: number;
  aiEnabled: boolean;
  escalated: boolean;
  onHandoffChange?: (state: HandoffState) => void;
  onEscalate?: () => void;
}

export const AIConversationControls: React.FC<AIConversationControlsProps> = ({
  conversationId,
  aiEnabled,
  escalated,
  onHandoffChange,
  onEscalate
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);

  // Escalation form state
  const [escalationReason, setEscalationReason] = useState('');
  const [escalationTrigger, setEscalationTrigger] = useState('explicit_request');
  const [assignToAgent, setAssignToAgent] = useState<number | null>(null);
  const [notifyAgent, setNotifyAgent] = useState(true);

  const open = Boolean(anchorEl);

  // Load available agents when escalation dialog opens
  React.useEffect(() => {
    if (escalateDialogOpen) {
      loadAvailableAgents();
    }
  }, [escalateDialogOpen]);

  const loadAvailableAgents = async () => {
    try {
      const response = await api.get(`/conversations/${conversationId}/available_agents`);
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Failed to load available agents:', error);
      setAgents([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle AI toggle
  const handleToggleAI = async () => {
    try {
      setLoading(true);
      const newState = !aiEnabled;

      const { data } = await api.put(`/conversations/${conversationId}/ai-toggle`, {
        ai_enabled: newState
      });

      setToggleDialogOpen(false);
      if (onHandoffChange) {
        onHandoffChange({
          ai_enabled: data.ai_enabled,
          escalated: data.escalated,
          escalated_at: data.escalated_at,
          escalated_by: data.escalated_by,
          escalation_reason: data.escalation_reason,
          escalation_trigger: data.escalation_trigger,
          assignee_id: data.assignee_id,
        });
      }
    } catch (error) {
      console.error('Failed to toggle AI:', error);
      alert('Failed to toggle AI mode');
    } finally {
      setLoading(false);
    }
  };

  // Handle manual escalation
  const handleEscalate = async () => {
    try {
      setLoading(true);

      const { data } = await api.post(`/conversations/${conversationId}/escalate`, {
        reason: escalationReason,
        assignee_id: assignToAgent,
      });

      setEscalateDialogOpen(false);
      setEscalationReason('');
      if (onHandoffChange) {
        onHandoffChange({
          ai_enabled: data.ai_enabled,
          escalated: data.escalated,
          escalated_at: data.escalated_at,
          escalated_by: data.escalated_by,
          escalation_reason: data.escalation_reason,
          escalation_trigger: data.escalation_trigger,
          assignee_id: data.assignee_id,
        });
      } else if (onEscalate) {
        onEscalate();
      }
    } catch (error) {
      console.error('Failed to escalate conversation:', error);
      alert('Failed to escalate conversation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box>
        {/* Quick AI Toggle Button */}
        <Tooltip title={aiEnabled ? 'Disable AI (Switch to Human)' : 'Enable AI Assistant'}>
          <IconButton
            onClick={() => setToggleDialogOpen(true)}
            color={aiEnabled ? 'primary' : 'default'}
            size="small"
          >
            {aiEnabled ? <SmartToy /> : <Person />}
          </IconButton>
        </Tooltip>

        {/* More Options Menu */}
        <Tooltip title="AI Options">
          <IconButton onClick={handleMenuOpen} size="small">
            <MoreVert />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => {
            handleMenuClose();
            setToggleDialogOpen(true);
          }}>
            <ListItemIcon>
              {aiEnabled ? <Person /> : <SmartToy />}
            </ListItemIcon>
            <ListItemText>
              {aiEnabled ? 'Switch to Human' : 'Enable AI'}
            </ListItemText>
          </MenuItem>

          {aiEnabled && !escalated && (
            <MenuItem onClick={() => {
              handleMenuClose();
              setEscalateDialogOpen(true);
            }}>
              <ListItemIcon>
                <PersonAdd />
              </ListItemIcon>
              <ListItemText>Escalate to Human</ListItemText>
            </MenuItem>
          )}

          <Divider />

          <MenuItem onClick={() => {
            handleMenuClose();
            // Navigate to AI settings (tab 6 in Settings page)
            window.location.href = '/settings?tab=6';
          }}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText>AI Configuration</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* AI Toggle Confirmation Dialog */}
      <Dialog
        open={toggleDialogOpen}
        onClose={() => setToggleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            {aiEnabled ? <Person color="action" /> : <SmartToy color="primary" />}
            <span>{aiEnabled ? 'Switch to Human Agent' : 'Enable AI Assistant'}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {aiEnabled
              ? 'Are you sure you want to disable AI and switch to human agent mode? This will require manual responses from your team.'
              : 'Enable AI assistant to automatically respond to customer messages. You can escalate to a human agent anytime.'}
          </Typography>

          {!aiEnabled && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'info.light',
                borderRadius: 1,
                mb: 2
              }}
            >
              <Typography variant="body2" color="info.dark">
                <AutoMode sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                AI will automatically escalate to human agents when needed based on your escalation rules.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleToggleAI}
            disabled={loading}
            color={aiEnabled ? 'warning' : 'primary'}
            startIcon={aiEnabled ? <Person /> : <SmartToy />}
          >
            {loading
              ? 'Processing...'
              : aiEnabled
              ? 'Switch to Human'
              : 'Enable AI'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Escalation Dialog */}
      <Dialog
        open={escalateDialogOpen}
        onClose={() => setEscalateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <span>Escalate to Human Agent</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manually escalate this conversation to a human agent. This will disable AI responses
            and assign the conversation to an available agent.
          </Typography>

          <Stack spacing={2.5}>
            {/* Reason */}
            <TextField
              fullWidth
              multiline
              rows={3}
              required
              label="Escalation Reason"
              placeholder="Why does this conversation need human assistance?"
              value={escalationReason}
              onChange={(e) => setEscalationReason(e.target.value)}
              helperText="Explain why this conversation needs human attention"
            />

            {/* Trigger Type */}
            <FormControl fullWidth>
              <InputLabel>Escalation Type</InputLabel>
              <Select
                value={escalationTrigger}
                onChange={(e) => setEscalationTrigger(e.target.value)}
                label="Escalation Type"
              >
                <MenuItem value="explicit_request">Customer Requested Human</MenuItem>
                <MenuItem value="confidence">Low AI Confidence</MenuItem>
                <MenuItem value="sentiment">Negative Sentiment</MenuItem>
                <MenuItem value="repetition">Repetitive Responses</MenuItem>
                <MenuItem value="manual">Manual Escalation</MenuItem>
              </Select>
            </FormControl>

            {/* Agent Assignment (Optional) */}
            <FormControl fullWidth>
              <InputLabel>Assign to Agent (Optional)</InputLabel>
              <Select
                value={assignToAgent || ''}
                onChange={(e) => setAssignToAgent(e.target.value ? Number(e.target.value) : null)}
                label="Assign to Agent (Optional)"
              >
                <MenuItem value="">Auto-assign (Round Robin)</MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.full_name || agent.name || `Agent ${agent.id}`}
                    {agent.availability && ` - ${agent.availability}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Notify Agent */}
            <FormControlLabel
              control={
                <Switch
                  checked={notifyAgent}
                  onChange={(e) => setNotifyAgent(e.target.checked)}
                />
              }
              label="Notify assigned agent"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscalateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleEscalate}
            disabled={loading || !escalationReason.trim()}
            startIcon={<PersonAdd />}
          >
            {loading ? 'Escalating...' : 'Escalate to Human'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIConversationControls;
